import { createStyles, Group, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { BackgroundJobOut } from '@repo/api-codegen';
import {
	useApiCompleteAutomationReview,
	useApiDeleteApiKey,
} from '@repo/api-codegen';
import type { ApiKeyOutV2 } from '@repo/api-codegen/v2';
import { apiListApiKeysV2QueryKey } from '@repo/api-codegen/v2';
import { trackEvent } from '@repo/common/analytics';
import { Icon, Text } from '@repo/foundations';
import { useQueryClient } from '@tanstack/react-query';
import { useEventListener, useMount, useUnmount } from 'ahooks';
import { find, isEmpty, isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { IComment, IncidentStatus } from '../../api';
import {
	useAuthUser,
	useCreateComment,
	useDeleteComment,
	useDeleteNotification,
	useNotification,
	useNotificationInfiniteList,
	useNotificationSettingsList,
	useUpdateComment,
	useUpdateNotificationSettings,
} from '../../api';
import { useAIEmbedded } from '../../api/hooks/ai/useAIEmbedded';
import { automationJobQueryKeyFactory } from '../../api/hooks/automationJob/constants';
import { useBackgroundJob } from '../../api/hooks/backgroundJob';
import { useIncident } from '../../api/hooks/monitoring';
import { useIncidentStatusUpdate } from '../../api/hooks/monitoring/useIncidentStatusUpdate';
import { notificationQueryKeyFactory } from '../../api/hooks/notification/constants';
import { useApplyAccessRequest } from '../../api/hooks/notification/useApplyAccessRequest';
import { useApplyChangeRequest } from '../../api/hooks/notification/useApplyChangeRequest';
import { getFilters } from '../../api/hooks/notification/utils';
import {
	useApproveTeamAccessRequest,
	useRejectTeamAccessRequest,
} from '../../api/hooks/team/accessRequests';
import type { IBackgroundJob } from '../../api/types/models/backgroundJob';
import type { INotification } from '../../api/types/models/notification';
import { BackgroundJobProgress } from '../../components/BackgroundJobProgress';
import { openConfirmModal, openModal } from '../../components/ModalManager';
import { ApiKeyModal } from '../../components/Settings/ApiSettingsPage/ApiKeyModal';
import type { BackgroundJob } from '../../lib/models/backgroundJob';
import type { WithOnlyIdRequired } from '../../lib/typescript';
import { deleteParamsFromUrl, setParamsInUrl } from '../../utils/url';
import { InboxList } from './components/InboxList';
import { InboxSkeleton } from './components/InboxSkeleton';
import { InboxView } from './components/InboxView';
import {
	ANALYTICS_EVENTS,
	APP_HEADER_HEIGHT,
	NOTIFICATION_STALE_TIME,
} from './constants';
import { useInboxEventTypes, useInboxFeature } from './hooks';
import { inboxFeatureStore } from './store';
import type { EnhancedNotification, NotificationQueryData } from './types';
import { getEventsFilter } from './utils/filters';
import {
	useDeleteAllNotificationsMutation,
	useMarkAsReadMutation,
	useRestoreAllNotificationsMutation,
	useRestoreNotificationMutation,
} from './utils/mutations';

const useStyles = createStyles((theme) => ({
	inboxPageStack: {
		height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
		borderTopWidth: 1,
		borderTopStyle: 'solid',
		borderTopColor: theme.other.getColor('border/secondary/default'),
	},
	tabsList: {
		borderBottomWidth: 1,
	},
}));

export const InboxController = observer(() => {
	const {
		searchTerm,
		setSearchTerm,
		events,
		setEvents,
		isInclusive,
		setIsInclusive,
		selectedId,
		setSelectedId,
	} = useInboxFeature();
	const { page, filters } = getFilters();
	const queryClient = useQueryClient();
	const { user, workspace } = useAuthUser();
	const navigate = useNavigate();
	const [opened, setOpened] = useState(false);
	const [filterOpened, setFilterOpened] = useState(false);
	const [filterSearchTerm, setFilterSearchTerm] = useState('');
	const [
		isCompleteAutomationReviewLoading,
		setIsCompleteAutomationReviewLoading,
	] = useState(false);
	const [backgroundJob, setBackgroundJob] = useState<BackgroundJobOut | null>(
		null
	);
	const [completeAutomationReviewJobId, setCompleteAutomationReviewJobId] =
		useState<string | undefined>(undefined);
	const inboxEventTypes = useInboxEventTypes();

	const { data: notificationSettingsData } = useNotificationSettingsList({});

	const { mutateAsync: updateNotificationSettings } =
		useUpdateNotificationSettings({});

	const {
		data,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
		isLoading: isNotificationListLoading,
	} = useNotificationInfiniteList({
		filters,
		options: {
			refetchOnMount: true,
			staleTime: NOTIFICATION_STALE_TIME,
		},
	});

	const notifications = useMemo(() => data?.pages || [], [data?.pages]);

	// Compute unread notifications from the main notifications data
	const unreadNotifications = useMemo(
		() =>
			notifications.filter((notification: INotification) => !notification.read),
		[notifications]
	);

	const filteredNotifications = useMemo(() => {
		if (inboxFeatureStore.showRead) {
			// When showRead is true, show all notifications (API handles filtering)
			return notifications;
		}
		// When showRead is false, show unread notifications + recently read notifications
		return notifications.filter(
			(notification: INotification) =>
				!notification.read || inboxFeatureStore.isRecentlyRead(notification.id)
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		notifications,
		inboxFeatureStore.showRead,
		inboxFeatureStore.recentlyRead,
	]);

	const enhancedNotifications = useMemo(
		() =>
			// Add AI agent metadata computation
			filteredNotifications.map((notification) => ({
				...notification,
				computedMetadata: {
					...((notification as any).computedMetadata || {}),
				},
			})) as unknown as EnhancedNotification[],
		[filteredNotifications]
	);

	const notificationFromList = useMemo(
		() => find(enhancedNotifications, { id: selectedId }),
		[enhancedNotifications, selectedId]
	);

	const useNotificationEnabled = useMemo(
		() => !isEmpty(selectedId) && isNil(notificationFromList),
		[selectedId, notificationFromList]
	);

	const { data: fetchedNotification, isLoading: isFetchingSelected } =
		useNotification({
			id: selectedId,
			options: {
				enabled: useNotificationEnabled,
			},
		});

	const selected = useMemo(() => {
		if (notificationFromList) {
			return notificationFromList;
		}
		if (fetchedNotification) {
			return fetchedNotification as EnhancedNotification;
		}
		return undefined;
	}, [notificationFromList, fetchedNotification]);

	const selectedIndex = useMemo(() => {
		if (!selected) return -1;
		return filteredNotifications.findIndex((n) => n.id === selected.id);
	}, [filteredNotifications, selected]);

	const listQueryKey = useMemo(
		() => notificationQueryKeyFactory.list(page, filters),
		[page, filters]
	);

	const { mutateAsync: markAsRead } = useMarkAsReadMutation();
	const { mutateAsync: restoreAllNotifications } =
		useRestoreAllNotificationsMutation();
	const { mutateAsync: restoreNotification } = useRestoreNotificationMutation();

	const handleRestore = useCallback(
		async ({ id }: { id: string }) => {
			await restoreNotification({ notificationId: id });
		},
		[restoreNotification]
	);

	const { mutate: deleteNotification } = useDeleteNotification({
		options: {
			onMutate: async ({ id }) => {
				// Cancel any outgoing refetches to avoid race conditions
				await queryClient.cancelQueries({
					queryKey: ['notification', 'notifications'],
				});

				// Get the previous data for rollback
				const previous = queryClient.getQueriesData({
					queryKey: ['notification', 'notifications'],
				});

				// Optimistically remove the notification from all query caches
				queryClient.setQueriesData<NotificationQueryData>(
					{ queryKey: ['notification', 'notifications'] },
					(old) => {
						if (!old?.pages) return old;
						return {
							...old,
							pages: old.pages.map((page) => ({
								...page,
								results: page.results.filter(
									(notification: INotification) => notification.id !== id
								),
							})),
						};
					}
				);

				return { previous };
			},
			onSuccess: () => {
				deleteParamsFromUrl('id');
			},
			onError: (_, __, context: unknown) => {
				// Rollback the optimistic update on error
				const mutationContext = context as { previous?: Array<[any, any]> };
				if (mutationContext?.previous) {
					mutationContext.previous.forEach(([queryKey, data]: [any, any]) => {
						queryClient.setQueryData(queryKey, data);
					});
				}
				showNotification({
					title: 'Error',
					message: 'Failed to delete notification',
					color: 'red',
				});
			},
		},
	});

	const { mutate: deleteAllNotifications } =
		useDeleteAllNotificationsMutation();

	const openEditKeyModal = useCallback((key: ApiKeyOutV2) => {
		openModal({
			title: 'Rename API key',
			children: <ApiKeyModal apiKey={key} />,
		});
	}, []);

	const { mutate: deleteKey } = useApiDeleteApiKey({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiListApiKeysV2QueryKey({}),
			});
		},
	});

	const handleDeleteKey = useCallback(
		(apiKey: ApiKeyOutV2) => {
			openConfirmModal({
				title: 'Delete API key?',
				children: <Text>You can't undo this action.</Text>,
				confirmProps: {
					variant: 'primary',
					tone: 'critical',
				},
				labels: { confirm: 'Delete API Key', cancel: 'Cancel' },
				onConfirm: () =>
					deleteKey({
						pathParams: { apiKeyId: apiKey.id },
					}),
			});
		},
		[deleteKey]
	);

	const handleDeleteAll = useCallback(() => {
		trackEvent(ANALYTICS_EVENTS.DELETE_ALL, {}, user, workspace!);

		const previous = queryClient.getQueryData(listQueryKey);
		queryClient.setQueryData(
			listQueryKey,
			(old: { results?: INotification[] } | undefined) => {
				if (!old?.results) return old;
				return { ...old, results: [] };
			}
		);

		deleteAllNotifications(
			{
				events: getEventsFilter(inboxEventTypes, events, true),
			},
			{
				onError: () => {
					// Only handle rollback on error - success notification is handled by the mutation
					queryClient.setQueryData(listQueryKey, previous);
				},
			}
		);
	}, [
		events,
		deleteAllNotifications,
		inboxEventTypes,
		user,
		workspace,
		queryClient,
		listQueryKey,
	]);

	const handleDeleteAllRead = useCallback(() => {
		trackEvent(ANALYTICS_EVENTS.DELETE_READ, {}, user, workspace!);
		deleteAllNotifications({
			events: getEventsFilter(inboxEventTypes, events, true),
			read: true,
		});
	}, [deleteAllNotifications, inboxEventTypes, user, workspace, events]);

	const handleMarkAsRead = useCallback(async () => {
		if (!isNil(selected)) {
			const notificationId = selected.id;
			const previousReadStatus = selected.read;

			try {
				await markAsRead({
					id: notificationId,
					status: !previousReadStatus,
				});
			} catch (error) {
				showNotification({
					title: 'Error',
					message: 'Failed to mark notification as read',
					color: 'red',
				});
			}
		}
	}, [selected, markAsRead]);

	const handleMarkAsReadFromList = useCallback(
		async (params: { id: string }) => {
			try {
				await markAsRead({
					...params,
					status: true,
				});
			} catch (error) {
				showNotification({
					title: 'Error',
					message: 'Failed to mark notification as read',
					color: 'red',
				});
			}
		},
		[markAsRead]
	);

	const handleDelete = useCallback(() => {
		if (!isNil(selected)) {
			trackEvent(ANALYTICS_EVENTS.DELETE, {}, user, workspace);

			// Determine next notification to select (prefer next, fallback to previous)
			const nextNotification =
				(selectedIndex >= 0 && selectedIndex < filteredNotifications.length - 1
					? filteredNotifications[selectedIndex + 1]
					: undefined) ??
				(selectedIndex > 0
					? filteredNotifications[selectedIndex - 1]
					: undefined);

			deleteNotification(
				{ id: selected.id },
				{
					onSuccess: () => {
						if (nextNotification?.id) {
							setSelectedId(nextNotification.id);
							setParamsInUrl('id', nextNotification.id);
						} else {
							setSelectedId('');
							deleteParamsFromUrl('id');
						}
					},
				}
			);
		}
	}, [
		deleteNotification,
		selected,
		filteredNotifications,
		selectedIndex,
		setSelectedId,
		user,
		workspace,
	]);

	const handleMarkAllAsRead = useCallback(async () => {
		trackEvent(ANALYTICS_EVENTS.MARK_ALL_READ, {}, user, workspace!);
		// When marking all as read, clear recently read since everything will be read
		if (!inboxFeatureStore.showRead) {
			inboxFeatureStore.clearRecentlyRead();
		}
		try {
			await markAsRead({
				id: undefined,
				all: true,
				eventFilter: getEventsFilter(inboxEventTypes, '', true),
				status: true,
			});
		} catch (error) {
			showNotification({
				title: 'Error',
				message: 'Failed to mark all notifications as read',
				color: 'red',
			});
		}
	}, [user, workspace, markAsRead, inboxEventTypes]);

	const handleSetEvents = useCallback(
		(value: string) => {
			trackEvent(
				ANALYTICS_EVENTS.FILTER,
				{
					events: value,
				},
				user,
				workspace!
			);
			// Update URL param
			setEvents(value);
			// Keep store in sync without effects
			inboxFeatureStore.syncEventsWithStore(value || '', inboxEventTypes);
		},
		[user, workspace, setEvents, inboxEventTypes]
	);

	// Keep a stable registration with latest handler
	const handleSetEventsRef = useRef(handleSetEvents);
	handleSetEventsRef.current = handleSetEvents;

	const handleSearchClick = useCallback(() => {
		trackEvent(ANALYTICS_EVENTS.SEARCH, {}, user, workspace!);
	}, [user, workspace]);

	const { classes } = useStyles();

	const syncEvents = useCallback(() => {
		inboxFeatureStore.syncEventsWithStore(events || '', inboxEventTypes);
	}, [events, inboxEventTypes]);

	// Register callback once and seed store from URL
	useMount(() => {
		inboxFeatureStore.registerEventsCallback((value: string) =>
			handleSetEventsRef.current(value)
		);
		syncEvents();
	});

	// Keep store in sync when navigating back/forward (URL param changes)
	useEventListener('popstate', () => {
		syncEvents();
	});

	// Ensure callback is cleared on unmount
	useUnmount(() => {
		inboxFeatureStore.registerEventsCallback(() => {});
	});

	const handleOnSelect = useCallback(
		async (id: string, read: boolean) => {
			const notification = enhancedNotifications.find((n) => n.id === id);
			trackEvent(
				ANALYTICS_EVENTS.NOTIFICATION_CLICK,
				{
					notificationId: id,
					notificationEvent: notification?.event || 'unknown',
					notificationResourceType: notification?.resource_type || 'unknown',
					wasRead: read.toString(),
				},
				user,
				workspace!
			);

			if (notification?.computedMetadata?.isDataAccessRequest) {
				trackEvent(
					ANALYTICS_EVENTS.DATA_ACCESS_REQUEST_VIEW,
					{
						notificationId: id,
						notificationEvent: notification.event,
						resourceType: notification.resource_type || 'unknown',
						requestId:
							notification.computedMetadata?.dataAccessRequest?.id || 'unknown',
					},
					user,
					workspace!
				);
			}

			setSelectedId(id);
			setParamsInUrl('id', id);

			if (!read) {
				// Keep it visible temporarily when showRead is off
				if (!inboxFeatureStore.showRead) {
					inboxFeatureStore.addToRecentlyRead(id);
				}
				try {
					await markAsRead({
						id,
						status: true,
					});
				} catch (error) {
					showNotification({
						title: 'Error',
						message: 'Failed to mark notification as read',
						color: 'red',
					});
				}
			}
		},
		[markAsRead, setSelectedId, user, workspace, enhancedNotifications]
	);

	const { mutate: applyChangeRequest, isApplied: changeRequestApplied } =
		useApplyChangeRequest({
			id: selected?.request?.id,
		});

	const { mutate: applyAccessRequest, isApplied: accessRequestApplied } =
		useApplyAccessRequest({
			id: selected?.request?.id,
		});
	const {
		mutateAsync: approveTeamAccessRequest,
		isPending: isApprovingTeamAccessRequest,
	} = useApproveTeamAccessRequest();
	const {
		mutateAsync: rejectTeamAccessRequest,
		isPending: isRejectingTeamAccessRequest,
	} = useRejectTeamAccessRequest();

	useEventListener('keydown', (event: KeyboardEvent) => {
		if (
			(event.ctrlKey || event.metaKey) &&
			event.key === 'z' &&
			!event.shiftKey
		) {
			event.preventDefault();
			const pendingAction = inboxFeatureStore.pendingUndoAction;
			if (pendingAction && pendingAction.data?.undoFn) {
				pendingAction.data.undoFn();
				inboxFeatureStore.clearUndoAction();
			}
		}
	});

	const onKeyDown = useCallback(
		async (event: React.KeyboardEvent<HTMLElement>) => {
			if (event.key === 'j' || event.key === 'ArrowDown') {
				event.preventDefault();
				const currentIndex = enhancedNotifications.findIndex(
					(n) => n.id === selected?.id
				);
				const nextIndex =
					currentIndex < 0
						? 0
						: Math.min(currentIndex + 1, enhancedNotifications.length - 1);
				const nextNotification = enhancedNotifications[nextIndex];
				if (nextNotification && nextNotification.id !== selected?.id) {
					await handleOnSelect(nextNotification.id, nextNotification.read);
				}
			} else if (event.key === 'k' || event.key === 'ArrowUp') {
				event.preventDefault();
				const currentIndex = enhancedNotifications.findIndex(
					(n) => n.id === selected?.id
				);
				const prevIndex = currentIndex < 0 ? 0 : Math.max(currentIndex - 1, 0);
				const prevNotification = enhancedNotifications[prevIndex];
				if (prevNotification && prevNotification.id !== selected?.id) {
					await handleOnSelect(prevNotification.id, prevNotification.read);
				}
			} else if (
				(event.key === 'Delete' || event.key === 'Backspace') &&
				selected?.id
			) {
				event.preventDefault();
				deleteNotification({
					id: selected.id,
				});
			} else if (event.key === 'u' && selected?.read) {
				event.preventDefault();
				// When marking as unread, remove from recently read
				if (!inboxFeatureStore.showRead) {
					inboxFeatureStore.removeFromRecentlyRead(selected.id);
				}
				try {
					await markAsRead({
						id: selected.id,
						status: false,
					});
				} catch (error) {
					showNotification({
						title: 'Error',
						message: 'Failed to mark notification as unread',
						color: 'red',
					});
				}
			}
		},
		[
			selected,
			enhancedNotifications,
			handleOnSelect,
			deleteNotification,
			markAsRead,
		]
	);

	const { data: incident } = useIncident({
		id: selected?.resource_id as string,
		options: {
			enabled: Boolean(selected?.resource_id),
		},
	});

	const embeddedPromptId = selected?.metadata?.embedded_prompt_id as string;
	const { data: embeddedPrompt, isLoading: isLoadingEmbeddedPrompt } =
		useAIEmbedded({
			id: embeddedPromptId,
			options: {
				enabled: !!embeddedPromptId,
			},
		});

	const { mutateAsync: updateIncident } = useIncidentStatusUpdate();

	const handleIncidentStatusUpdate = useCallback(
		async (status: Exclude<IncidentStatus, 'active' | 'expired'>) => {
			if (!incident?.id) {
				return;
			}

			await updateIncident({
				data: {
					id: incident.id,
					monitorId: incident.monitor,
					status,
				},
			});

			if (status === 'resolved') {
				queryClient.invalidateQueries({
					queryKey: notificationQueryKeyFactory.allLists(),
				});
			}
		},
		[incident, updateIncident, queryClient]
	);

	const { mutateAsync: createCommentMutation } = useCreateComment({});
	const { mutateAsync: updateCommentMutation } = useUpdateComment({});
	const { mutateAsync: deleteCommentMutation } = useDeleteComment({});
	const completeAutomationReviewMutation = useApiCompleteAutomationReview({
		onMutate: () => {
			setIsCompleteAutomationReviewLoading(true);
		},
		onSuccess: (response) => {
			// Response should contain the job ID
			if (response.background_job) {
				setBackgroundJob(response.background_job);
			} else {
				setIsCompleteAutomationReviewLoading(false);
			}
		},
		onError: () => {
			setIsCompleteAutomationReviewLoading(false);
			showNotification({
				title: 'Could not complete automation review',
				message: 'Please contact customer support if the issue continues',
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		},
	});

	// Poll the background job until completion
	useBackgroundJob({
		id: backgroundJob?.id || '',
		options: {
			enabled:
				!isNil(completeAutomationReviewJobId) && !isNil(backgroundJob?.id),
			refetchInterval: (query) => {
				const { data } = query.state;

				if (data?.failed) {
					setIsCompleteAutomationReviewLoading(false);
					showNotification({
						title: 'Automation review completion failed',
						message: 'Please contact customer support if the issue continues',
						color: 'red',
						icon: (
							<Icon name="alertCircle" color="text/critical-on-fill/default" />
						),
					});
					setCompleteAutomationReviewJobId(undefined);
					return false;
				}

				if (data?.completed) {
					setIsCompleteAutomationReviewLoading(false);
					setCompleteAutomationReviewJobId(undefined);
					return false;
				}

				// Continue polling every second while job is running
				return 1000;
			},
		},
	});

	const handleCompleteAutomationReview = (automationJobId: string) => {
		setCompleteAutomationReviewJobId(automationJobId);
		completeAutomationReviewMutation.mutate({
			pathParams: {
				automationJobId,
			},
		});
	};

	const handleBackgroundJobComplete = (result?: IBackgroundJob) => {
		if (result?.failed) {
			showNotification({
				title: 'Error running automation',
				message: 'An error has occurred while running the automation',
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		} else {
			showNotification({
				title: 'Automation changes applied successfully',
				message: 'Automation successfully applied changes after review',
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
				closeButtonProps: {
					'data-testid': 'automation-review-success-close-button',
				},
			});
		}

		setBackgroundJob(null);
		queryClient.invalidateQueries({
			queryKey: automationJobQueryKeyFactory.all(),
		});
		queryClient.invalidateQueries({
			queryKey: notificationQueryKeyFactory.allLists(),
		});
	};

	const commentActions = useMemo(() => {
		if (!selected || !selected.computedMetadata.isComment) return undefined;
		const entityId = selected.resource_id;
		return {
			handleCreateComment: async (data: Partial<Omit<IComment, 'id'>>) =>
				createCommentMutation({
					data: {
						entity: entityId,
						...data,
					},
				}),
			handleUpdateComment: async (data: WithOnlyIdRequired<IComment>) => {
				await updateCommentMutation({
					data,
				});
			},
			handleDeleteComment: async (id: string): Promise<void> => {
				await deleteCommentMutation({ id });
			},
		};
	}, [
		selected,
		createCommentMutation,
		updateCommentMutation,
		deleteCommentMutation,
	]);

	if (isNotificationListLoading) {
		return <InboxSkeleton />;
	}

	return (
		<>
			<Stack className={classes.inboxPageStack} spacing={0}>
				<Group spacing={0} noWrap>
					<InboxList
						data={filteredNotifications}
						selected={selected}
						selectedIndex={selectedIndex}
						setSelectedId={setSelectedId}
						searchTerm={searchTerm}
						setSearchTerm={setSearchTerm}
						events={events}
						setEvents={handleSetEvents}
						isInclusive={isInclusive}
						setIsInclusive={setIsInclusive}
						hasNextPage={hasNextPage}
						fetchNextPage={fetchNextPage}
						isFetchingNextPage={isFetchingNextPage}
						markAsRead={handleMarkAsReadFromList}
						markAsReadMutation={markAsRead}
						inboxEventTypes={inboxEventTypes}
						opened={opened}
						setOpened={setOpened}
						handleDeleteAll={handleDeleteAll}
						handleDeleteAllRead={handleDeleteAllRead}
						handleMarkAllAsRead={handleMarkAllAsRead}
						handleDelete={handleDelete}
						handleMarkAsRead={handleMarkAsRead}
						navigate={navigate}
						queryClient={queryClient}
						notificationSettingsData={notificationSettingsData}
						updateNotificationSettings={updateNotificationSettings}
						user={user}
						workspace={workspace}
						onSearchClick={handleSearchClick}
						isLoading={isNotificationListLoading}
						filterOpened={filterOpened}
						setFilterOpened={setFilterOpened}
						filterSearchTerm={filterSearchTerm}
						setFilterSearchTerm={setFilterSearchTerm}
						unreadNotifications={unreadNotifications}
						onKeyDown={onKeyDown}
						handleOnSelect={handleOnSelect}
						enhancedNotifications={enhancedNotifications}
						restoreAllNotifications={restoreAllNotifications}
						handleRestore={handleRestore}
					/>
					<InboxView
						selected={selected}
						setSelectedId={setSelectedId}
						data={enhancedNotifications}
						searchTerm={searchTerm}
						events={events}
						isLoading={useNotificationEnabled && isFetchingSelected}
						inboxEventTypes={inboxEventTypes}
						onKeyDown={onKeyDown}
						deleteNotification={deleteNotification}
						applyChangeRequest={applyChangeRequest}
						applyAccessRequest={applyAccessRequest}
						changeRequestApplied={changeRequestApplied}
						accessRequestApplied={accessRequestApplied}
						incident={incident}
						onUpdateIncident={handleIncidentStatusUpdate}
						commentActions={commentActions}
						handleDeleteKey={handleDeleteKey}
						openEditKeyModal={openEditKeyModal}
						user={user}
						workspace={workspace}
						isCompleteAutomationReviewLoading={
							isCompleteAutomationReviewLoading
						}
						completeAutomationReviewMutation={handleCompleteAutomationReview}
						embeddedPrompt={embeddedPrompt}
						isLoadingEmbeddedPrompt={isLoadingEmbeddedPrompt}
						approveTeamAccessRequest={approveTeamAccessRequest}
						rejectTeamAccessRequest={rejectTeamAccessRequest}
						isApprovingTeamAccessRequest={isApprovingTeamAccessRequest}
						isRejectingTeamAccessRequest={isRejectingTeamAccessRequest}
					/>
				</Group>
			</Stack>
			{backgroundJob && (
				<BackgroundJobProgress
					key={backgroundJob.id}
					job={backgroundJob as unknown as BackgroundJob}
					onCompleted={handleBackgroundJobComplete}
				/>
			)}
		</>
	);
});
